// File Location: src/services/settingsService.js

import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  writeBatch
} from "firebase/firestore";
import { db } from "./firebaseConfig";

// Settings Collections
const SETTINGS_COLLECTION = "systemSettings";
const SERVICES_COLLECTION = "availableServices";
const SETTINGS_HISTORY_COLLECTION = "settingsHistory";

// Get system settings
export const getSystemSettings = async () => {
  try {
    const settingsDoc = await getDoc(doc(db, SETTINGS_COLLECTION, "main"));
    
    if (settingsDoc.exists()) {
      return {
        success: true,
        settings: settingsDoc.data(),
      };
    } else {
      // Return default settings if none exist
      return {
        success: true,
        settings: getDefaultSettings(),
      };
    }
  } catch (error) {
    console.error("Error fetching system settings:", error);
    return {
      success: false,
      error: error.message,
      settings: getDefaultSettings(),
    };
  }
};

// Update system settings
export const updateSystemSettings = async (section, data, updatedBy) => {
  try {
    const settingsRef = doc(db, SETTINGS_COLLECTION, "main");
    const currentSettings = await getDoc(settingsRef);
    
    let updatedSettings = {};
    if (currentSettings.exists()) {
      updatedSettings = currentSettings.data();
    }

    // Update the specific section
    updatedSettings[section] = {
      ...updatedSettings[section],
      ...data,
      lastUpdated: new Date(),
      updatedBy: updatedBy,
    };

    updatedSettings.lastModified = new Date();
    updatedSettings.lastModifiedBy = updatedBy;

    await setDoc(settingsRef, updatedSettings, { merge: true });

    // Log the change in history
    await addDoc(collection(db, SETTINGS_HISTORY_COLLECTION), {
      section: section,
      changes: data,
      updatedBy: updatedBy,
      timestamp: new Date(),
      action: "update",
    });

    return {
      success: true,
      message: "Settings updated successfully",
    };
  } catch (error) {
    console.error("Error updating system settings:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Get available services
export const getAvailableServices = async () => {
  try {
    const servicesQuery = query(
      collection(db, SERVICES_COLLECTION),
      orderBy("name")
    );
    
    const querySnapshot = await getDocs(servicesQuery);
    const services = [];

    querySnapshot.forEach((doc) => {
      services.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return {
      success: true,
      services,
    };
  } catch (error) {
    console.error("Error fetching available services:", error);
    return {
      success: false,
      error: error.message,
      services: [],
    };
  }
};

// Get active services only
export const getActiveServices = async () => {
  try {
    const servicesQuery = query(
      collection(db, SERVICES_COLLECTION),
      where("isActive", "==", true),
      orderBy("name")
    );
    
    const querySnapshot = await getDocs(servicesQuery);
    const services = [];

    querySnapshot.forEach((doc) => {
      services.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return {
      success: true,
      services,
    };
  } catch (error) {
    console.error("Error fetching active services:", error);
    return {
      success: false,
      error: error.message,
      services: [],
    };
  }
};

// Add new service
export const addService = async (serviceData) => {
  try {
    const serviceDoc = {
      ...serviceData,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const docRef = await addDoc(collection(db, SERVICES_COLLECTION), serviceDoc);

    // Log the addition
    await addDoc(collection(db, SETTINGS_HISTORY_COLLECTION), {
      section: "services",
      action: "add",
      serviceId: docRef.id,
      serviceName: serviceData.name,
      updatedBy: serviceData.createdBy,
      timestamp: new Date(),
    });

    return {
      success: true,
      service: {
        id: docRef.id,
        ...serviceDoc,
      },
      message: "Service added successfully",
    };
  } catch (error) {
    console.error("Error adding service:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Update service
export const updateService = async (serviceId, updateData) => {
  try {
    const serviceRef = doc(db, SERVICES_COLLECTION, serviceId);
    const currentService = await getDoc(serviceRef);

    if (!currentService.exists()) {
      return {
        success: false,
        error: "Service not found",
      };
    }

    const updatedServiceData = {
      ...updateData,
      updatedAt: new Date(),
    };

    await updateDoc(serviceRef, updatedServiceData);

    // Log the update
    await addDoc(collection(db, SETTINGS_HISTORY_COLLECTION), {
      section: "services",
      action: "update",
      serviceId: serviceId,
      serviceName: currentService.data().name,
      changes: updateData,
      updatedBy: updateData.updatedBy,
      timestamp: new Date(),
    });

    return {
      success: true,
      message: "Service updated successfully",
    };
  } catch (error) {
    console.error("Error updating service:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Delete service
export const deleteService = async (serviceId, deletedBy) => {
  try {
    const serviceRef = doc(db, SERVICES_COLLECTION, serviceId);
    const serviceDoc = await getDoc(serviceRef);

    if (!serviceDoc.exists()) {
      return {
        success: false,
        error: "Service not found",
      };
    }

    const serviceData = serviceDoc.data();

    // Instead of hard delete, mark as deleted
    await updateDoc(serviceRef, {
      isDeleted: true,
      deletedAt: new Date(),
      deletedBy: deletedBy,
      isActive: false,
    });

    // Log the deletion
    await addDoc(collection(db, SETTINGS_HISTORY_COLLECTION), {
      section: "services",
      action: "delete",
      serviceId: serviceId,
      serviceName: serviceData.name,
      deletedBy: deletedBy,
      timestamp: new Date(),
    });

    return {
      success: true,
      message: "Service deleted successfully",
    };
  } catch (error) {
    console.error("Error deleting service:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Bulk update services
export const bulkUpdateServices = async (updates, updatedBy) => {
  try {
    const batch = writeBatch(db);
    
    updates.forEach(({ serviceId, updateData }) => {
      const serviceRef = doc(db, SERVICES_COLLECTION, serviceId);
      batch.update(serviceRef, {
        ...updateData,
        updatedAt: new Date(),
        updatedBy: updatedBy,
      });
    });

    await batch.commit();

    // Log bulk update
    await addDoc(collection(db, SETTINGS_HISTORY_COLLECTION), {
      section: "services",
      action: "bulk_update",
      updatedCount: updates.length,
      updatedBy: updatedBy,
      timestamp: new Date(),
    });

    return {
      success: true,
      message: `${updates.length} services updated successfully`,
    };
  } catch (error) {
    console.error("Error in bulk update:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Get services by category
export const getServicesByCategory = async (category) => {
  try {
    const servicesQuery = query(
      collection(db, SERVICES_COLLECTION),
      where("category", "==", category),
      where("isActive", "==", true),
      orderBy("name")
    );
    
    const querySnapshot = await getDocs(servicesQuery);
    const services = [];

    querySnapshot.forEach((doc) => {
      services.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return {
      success: true,
      services,
    };
  } catch (error) {
    console.error("Error fetching services by category:", error);
    return {
      success: false,
      error: error.message,
      services: [],
    };
  }
};

// Get service categories
export const getServiceCategories = async () => {
  try {
    const servicesQuery = query(
      collection(db, SERVICES_COLLECTION),
      where("isActive", "==", true)
    );
    
    const querySnapshot = await getDocs(servicesQuery);
    const categories = new Set();

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.category) {
        categories.add(data.category);
      }
    });

    return {
      success: true,
      categories: Array.from(categories).sort(),
    };
  } catch (error) {
    console.error("Error fetching service categories:", error);
    return {
      success: false,
      error: error.message,
      categories: [],
    };
  }
};

// Get settings history
export const getSettingsHistory = async (limit = 50) => {
  try {
    const historyQuery = query(
      collection(db, SETTINGS_HISTORY_COLLECTION),
      orderBy("timestamp", "desc")
    );
    
    const querySnapshot = await getDocs(historyQuery);
    const history = [];

    querySnapshot.forEach((doc) => {
      history.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return {
      success: true,
      history: history.slice(0, limit),
    };
  } catch (error) {
    console.error("Error fetching settings history:", error);
    return {
      success: false,
      error: error.message,
      history: [],
    };
  }
};

// Reset settings to default
export const resetSettingsToDefault = async (section, resetBy) => {
  try {
    const defaultSettings = getDefaultSettings();
    
    if (section === "all") {
      const settingsRef = doc(db, SETTINGS_COLLECTION, "main");
      await setDoc(settingsRef, {
        ...defaultSettings,
        lastModified: new Date(),
        lastModifiedBy: resetBy,
      });
    } else {
      await updateSystemSettings(section, defaultSettings[section], resetBy);
    }

    // Log the reset
    await addDoc(collection(db, SETTINGS_HISTORY_COLLECTION), {
      section: section,
      action: "reset_to_default",
      updatedBy: resetBy,
      timestamp: new Date(),
    });

    return {
      success: true,
      message: "Settings reset to default successfully",
    };
  } catch (error) {
    console.error("Error resetting settings:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Export settings
export const exportSettings = async () => {
  try {
    const settings = await getSystemSettings();
    const services = await getAvailableServices();

    const exportData = {
      settings: settings.settings,
      services: services.services,
      exportedAt: new Date().toISOString(),
      version: "1.0",
    };

    return {
      success: true,
      data: exportData,
    };
  } catch (error) {
    console.error("Error exporting settings:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Import settings
export const importSettings = async (importData, importedBy) => {
  try {
    const batch = writeBatch(db);

    // Import system settings
    if (importData.settings) {
      const settingsRef = doc(db, SETTINGS_COLLECTION, "main");
      batch.set(settingsRef, {
        ...importData.settings,
        lastModified: new Date(),
        lastModifiedBy: importedBy,
        importedAt: new Date(),
      });
    }

    // Import services
    if (importData.services && Array.isArray(importData.services)) {
      // First, get existing services to avoid duplicates
      const existingServices = await getAvailableServices();
      const existingServiceNames = existingServices.services.map(s => s.name.toLowerCase());

      importData.services.forEach((service) => {
        // Only import if service doesn't already exist
        if (!existingServiceNames.includes(service.name.toLowerCase())) {
          const serviceRef = doc(collection(db, SERVICES_COLLECTION));
          batch.set(serviceRef, {
            ...service,
            createdAt: new Date(),
            updatedAt: new Date(),
            importedBy: importedBy,
            importedAt: new Date(),
          });
        }
      });
    }

    await batch.commit();

    // Log the import
    await addDoc(collection(db, SETTINGS_HISTORY_COLLECTION), {
      section: "all",
      action: "import",
      importedBy: importedBy,
      timestamp: new Date(),
      importedItemsCount: {
        settings: importData.settings ? 1 : 0,
        services: importData.services ? importData.services.length : 0,
      },
    });

    return {
      success: true,
      message: "Settings imported successfully",
    };
  } catch (error) {
    console.error("Error importing settings:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Validate service data
export const validateServiceData = (serviceData) => {
  const errors = [];

  if (!serviceData.name || serviceData.name.trim().length === 0) {
    errors.push("Service name is required");
  }

  if (serviceData.name && serviceData.name.length > 100) {
    errors.push("Service name must be less than 100 characters");
  }

  if (serviceData.description && serviceData.description.length > 500) {
    errors.push("Description must be less than 500 characters");
  }

  const validCategories = [
    "Healthcare", "Insurance", "Finance", "Documentation", 
    "Verification", "Processing", "Support", "Analytics"
  ];
  
  if (serviceData.category && !validCategories.includes(serviceData.category)) {
    errors.push("Invalid service category");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Get default settings
const getDefaultSettings = () => ({
  general: {
    siteName: "Healthcare Management Platform",
    siteDescription: "Comprehensive healthcare management and insurance processing platform",
    supportEmail: "support@example.com",
    supportPhone: "+1 (555) 123-4567",
    businessHours: "9:00 AM - 6:00 PM (Mon-Fri)",
    address: "",
    timeZone: "UTC",
    dateFormat: "MM/DD/YYYY",
    currency: "USD",
  },
  email: {
    smtpHost: "",
    smtpPort: "587",
    smtpUser: "",
    smtpPassword: "",
    fromEmail: "",
    fromName: "",
    enableEmailNotifications: true,
    welcomeEmailTemplate: "Welcome to our platform! Your account has been created successfully.",
    approvalEmailTemplate: "Congratulations! Your application has been approved.",
    rejectionEmailTemplate: "We regret to inform you that your application has been rejected.",
  },
  notifications: {
    enablePushNotifications: true,
    enableEmailNotifications: true,
    enableSMSNotifications: false,
    notificationRetentionDays: 30,
  },
  security: {
    sessionTimeoutMinutes: 60,
    passwordMinLength: 8,
    requireSpecialCharacters: true,
    requireNumbers: true,
    maxLoginAttempts: 5,
    accountLockoutMinutes: 15,
  },
  appearance: {
    primaryColor: "#0D9488",
    secondaryColor: "#06B6D4",
    logoUrl: "",
    faviconUrl: "",
    customCSS: "",
  },
});

// Get setting value by key
export const getSettingValue = async (section, key, defaultValue = null) => {
  try {
    const settings = await getSystemSettings();
    if (settings.success && settings.settings[section] && settings.settings[section][key] !== undefined) {
      return {
        success: true,
        value: settings.settings[section][key],
      };
    } else {
      return {
        success: true,
        value: defaultValue,
      };
    }
  } catch (error) {
    console.error("Error getting setting value:", error);
    return {
      success: false,
      error: error.message,
      value: defaultValue,
    };
  }
};

// Update single setting value
export const updateSettingValue = async (section, key, value, updatedBy) => {
  try {
    const currentSettings = await getSystemSettings();
    if (!currentSettings.success) {
      throw new Error("Failed to get current settings");
    }

    const sectionData = currentSettings.settings[section] || {};
    sectionData[key] = value;

    return await updateSystemSettings(section, sectionData, updatedBy);
  } catch (error) {
    console.error("Error updating setting value:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};